// Stellarium Web - Copyright (c) 2022 - Stellarium Labs SRL
//
// This program is licensed under the terms of the GNU AGPL v3, or
// alternatively under a commercial licence.
//
// The terms of the AGPL v3 license can be found in the main directory of this
// repository.

import Vue from 'vue'
import _ from 'lodash'
import StelWebEngine from '@/assets/js/stellarium-web-engine.js'
import { CONSTELLATION_ZH, SOLAR_ZH, SOLAR_TRAD, STAR_ZH, GUI_ZH } from '@/assets/zh-sky-names.js'

// [guanxing patch] M-QZ：七政四余模块状态。
//  qzTradNames - 七政传统名开关（translateFn 每帧被引擎回调，翻转即生效）
//  qzsyTimer   - 四余位置刷新 interval（3s）
//  qzsyObjs    - 四余 circle 的 SweObj 引用（key → obj）
//  qzsyPtrs    - 四余 circle 的引擎对象指针（.v），供选中路径按指针识别
let qzTradNames = false
let qzsyTimer = null
let qzsyObjs = null
let qzsyPtrs = null

// [guanxing patch] M-SW38：移动端 FOV 钳制旁路（导出星图用）。onReady 里的
// onValueChanged('fov') 守卫把视场钳到 ≤120°（防双指缩到全天空小球），但
// 「导出星图」需要临时把 core.fov 抬到 180°（天顶全景、囊括整个天球）——
// 120° 视场切不全半球。钳制逻辑在 initStelWebEngine 闭包内，引擎侧无开关，
// 故在此加一个模块级旁路标志：导出流程前置 false，恢复时置回 true。
let fovClampEnabled = true

var DDDate = Date
DDDate.prototype.getJD = function () {
  return (this.getTime() / 86400000) + 2440587.5
}

DDDate.prototype.setJD = function (jd) {
  this.setTime((jd - 2440587.5) * 86400000)
}

DDDate.prototype.getMJD = function () {
  return this.getJD() - 2400000.5
}

DDDate.prototype.setMJD = function (mjd) {
  this.setJD(mjd + 2400000.5)
}

const swh = {
  initStelWebEngine: function (store, wasmFile, canvasElem, callBackOnDone) {
    StelWebEngine({
      wasmFile: wasmFile,
      canvas: canvasElem,
      // [guanxing patch] 引擎 sys_translate 回调：按域查简体中文表，
      // 未收录的字符串原样返回（回退英文，不做机翻）。
      // gui 域：引擎线名标签（lines.c 经 sys_translate("gui", …)，M-SW3h）。
      translateFn: function (domain, str) {
        if (domain === 'gui' && GUI_ZH[str]) return GUI_ZH[str]
        // [guanxing patch] M-QZ：七政四余开启时，日月五星标签优先用传统名。
        // 行星标签每帧经 sys_translate("sky", …) 重取（planets.c），标志
        // 翻转下一帧即生效，无需引擎干预。
        if (domain === 'sky') {
          if (qzTradNames && SOLAR_TRAD[str]) return SOLAR_TRAD[str]
          if (SOLAR_ZH[str]) return SOLAR_ZH[str]
        }
        if (domain === 'skyculture') {
          if (CONSTELLATION_ZH[str]) return CONSTELLATION_ZH[str]
          if (STAR_ZH[str]) return STAR_ZH[str]
        }
        return str
      },
      onReady: function (lstel) {
        // [guanxing patch] 引擎语言设为 zh：skycultures.c 依据 sys_get_lang()
        // 决定名称走 native 直出还是翻译回调，必须在 skyculture 解析前设置。
        lstel.ccall('sys_set_lang', null, ['string'], ['zh'])
        store.commit('replaceStelWebEngine', lstel.getTree())
        // [guanxing patch] M-SW26：移动端缩放适配——把视场 fov 钳到不超过
        // 打开时的 120° 广角，解决「双指能一路缩到全天空小球」。根因：引擎
        // on_pinch 直接令 fov = start_fov / pinch 且不夹上限，属性 setter 只
        // 夹到投影 max_fov（立体投影 = 360°）。此处借 onValueChanged('fov')
        // 同步回调把越界的 core.fov 收回（无需重编引擎 WASM）。clampFovGuard
        // 防重入：回设会再触发一次同路径回调，值已合规、直接跳过。
        const MAX_FOV = 120 * Math.PI / 180
        let clampFovGuard = false
        lstel.onValueChanged(function (path, value) {
          const tree = store.state.stel
          if (path === 'fov' && typeof value === 'number' && !clampFovGuard &&
              fovClampEnabled && value > MAX_FOV) {
            clampFovGuard = true
            lstel.core.fov = MAX_FOV
            clampFovGuard = false
            value = MAX_FOV
          }
          _.set(tree, path, value)
          store.commit('replaceStelWebEngine', tree)
        })
        Vue.prototype.$stel = lstel
        Vue.prototype.$selectionLayer = lstel.createLayer({ id: 'slayer', z: 50, visible: true })
        Vue.prototype.$observingLayer = lstel.createLayer({ id: 'obslayer', z: 40, visible: true })
        Vue.prototype.$skyHintsLayer = lstel.createLayer({ id: 'skyhintslayer', z: 38, visible: true })
        callBackOnDone()
      }
    })
  },

  // ─── [guanxing patch] M-QZ：七政四余 ───────────────────────────────────
  // 四余 = 黄道上四个移动虚拟点：罗睺（月道平升交点）/计都（降交点）/
  // 紫炁（月亮平近地点）/月孛（远地点，近点对冲）。用引擎 circle 天体
  // 实现（frame=FRAME_ECLIPTIC=8 当日黄道；pos w=0 表无穷远方向点），
  // 渲染时自动注册点击区与标签，可点击出信息卡。
  // 注意：createObj 的 args 经 writeAsciiToMemory 传引擎，仅 ASCII 安全，
  // 中文 label 必须创建后经属性 setter（obj_call_json_str 走 UTF-8）设置。
  QZSY_DEFS: [
    { key: 'luohou', names: ['QZ luohou', 'Rahu'], label: '罗睺', meaning: '月道升交点（天首），日月食所起' },
    { key: 'jidou', names: ['QZ jidou', 'Ketu'], label: '计都', meaning: '月道降交点（天尾），与罗睺对冲' },
    { key: 'ziqi', names: ['QZ ziqi', 'Ziqi'], label: '紫炁', meaning: '月亮近地点（月行最速处）' },
    { key: 'yuebei', names: ['QZ yuebei', 'Yuebei'], label: '月孛', meaning: '月亮远地点（月行最迟处）' }
  ],

  // 四余当日黄道平经度（度）。平轨道根数（Meeus/Chapront）：
  //   Ω = 125.0445479 − 1934.1362891·T + 0.0020754·T²   （平升交点，18.6 年西移一周）
  //   ϖ = 83.3532465 + 4069.0137287·T − 0.0103200·T²    （平近点，8.85 年一周）
  // 入参 jd 为儒略日数（引擎时间经 observer.tt + 2400000.5 取得，随时间
  // 流动 / 时间旅行同步）；T 为自 J2000 起的儒略世纪数；紫炁=ϖ、月孛=ϖ+180
  // 为主流排盘软件口径。
  qzsyLon: function (jd) {
    const T = (jd - 2451545.0) / 36525
    const omega = 125.0445479 - 1934.1362891 * T + 0.0020754 * T * T
    const peri = 83.3532465 + 4069.0137287 * T - 0.0103200 * T * T
    const mod = (d) => ((d % 360) + 360) % 360
    return {
      luohou: mod(omega),
      jidou: mod(omega + 180),
      ziqi: mod(peri),
      yuebei: mod(peri + 180)
    }
  },

  // 懒创建四余图层与四个 circle 天体（首次启用时调用一次）。
  ensureQzsyLayer: function (stel) {
    if (Vue.prototype.$qzsyLayer) return
    // z 取 45：介于 observing(40) 与 selection(50) 之间，不遮选中高亮。
    Vue.prototype.$qzsyLayer = stel.createLayer({ id: 'qzsylayer', z: 45, visible: true })
    qzsyObjs = {}
    qzsyPtrs = {}
    for (const def of this.QZSY_DEFS) {
      const obj = stel.createObj('circle', {
        names: def.names,
        frame: 8,
        pos: [1, 0, 0, 0],
        size: [0.01, 0.01],
        color: [0.9, 0.25, 0.2, 0.35],
        border_color: [1, 0.55, 0.4, 1]
      })
      Vue.prototype.$qzsyLayer.add(obj)
      obj.label = def.label
      qzsyObjs[def.key] = obj
      qzsyPtrs[def.key] = obj.v
    }
    // 暴露引用供运行时检查/验收：circle 无 id，SweObj 的 children 枚举
    // （obj_foreach_child 跳过无 id 子对象）拿不到它们，必须走此映射。
    Vue.prototype.$qzsyObjs = qzsyObjs
  },

  // 刷新四余位置与标记尺寸。size 按 fov 比例缩放保持屏上大小近似恒定。
  updateQzsy: function () {
    const stel = Vue.prototype.$stel
    if (!qzsyObjs || !stel || !Vue.prototype.$qzsyLayer) return
    const r = Math.max(stel.core.fov * 0.012, 0.002)
    // 跟随引擎时间（observer.tt，MJD）：时间流动/时间旅行时四余点同步移动。
    const lon = this.qzsyLon(stel.core.observer.tt + 2400000.5)
    for (const def of this.QZSY_DEFS) {
      const lam = lon[def.key] * Math.PI / 180
      const obj = qzsyObjs[def.key]
      obj.pos = [Math.cos(lam), Math.sin(lam), 0, 0]
      obj.size = [r, r]
    }
  },

  // [guanxing patch] M-SW38：移动端 FOV 钳制旁路开关（导出星图临时抬 fov 到
  // 180° 用，见本文件顶部 fovClampEnabled 注释）。用法：导出前置 false，导出
  // 恢复视角后必须置回 true（finally 保证），否则移动端双指缩放恢复越界。
  setFovClampEnabled: function (enabled) {
    fovClampEnabled = !!enabled
  },

  // 图层菜单「七政四余」开关入口。off 仅淡出隐藏，对象保留，再开零重建。
  setQzsyEnabled: function (stel, on) {
    if (on) {
      this.ensureQzsyLayer(stel)
      Vue.prototype.$qzsyLayer.visible = true
      this.updateQzsy()
      if (!qzsyTimer) {
        qzsyTimer = setInterval(() => { this.updateQzsy() }, 3000)
      }
      // 保证七政标签可见（行星标签被用户关掉时传统名无从展示）
      stel.core.planets.hints_visible = true
      qzTradNames = true
    } else {
      if (qzsyTimer) {
        clearInterval(qzsyTimer)
        qzsyTimer = null
      }
      if (Vue.prototype.$qzsyLayer) Vue.prototype.$qzsyLayer.visible = false
      qzTradNames = false
    }
  },

  // 信息卡辅助：识别四余 SkySource（合成来源 model='circle' 且
  // names[0] 为中文标签），命中返回定义项，未命中返回 null。
  qzsyDefForSkySource: function (ss) {
    if (!ss || ss.model !== 'circle' || !ss.names || !ss.names.length) return null
    return this.QZSY_DEFS.find(d => d.label === ss.names[0]) || null
  },
  // ───────────────────────────────────────────────────────────────

  monthNames: ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ],

  astroConstants: {
    // Light time for 1 au in s
    ERFA_AULT: 499.004782,
    // Seconds per day
    ERFA_DAYSEC: 86400.0,
    // Days per Julian year
    ERFA_DJY: 365.25,
    // Astronomical unit in m
    ERFA_DAU: 149597870000
  },

  iconForSkySourceTypes: function (skySourceTypes) {
    // Array sorted by specificity, i.e. the most generic names at the end
    const iconForType = {
      // Stars
      'Pec?': 'star',
      '**?': 'double_star',
      '**': 'double_star',
      'V*': 'variable_star',
      'V*?': 'variable_star',
      '*': 'star',

      // Candidates
      'As?': 'group_of_stars',
      'SC?': 'group_of_galaxies',
      'Gr?': 'group_of_galaxies',
      'C?G': 'group_of_galaxies',
      'G?': 'galaxy',

      // Multiple objects
      reg: 'region_defined_in_the_sky',
      SCG: 'group_of_galaxies',
      ClG: 'group_of_galaxies',
      GrG: 'group_of_galaxies',
      IG: 'interacting_galaxy',
      PaG: 'pair_of_galaxies',
      'C?*': 'open_galactic_cluster',
      'Gl?': 'globular_cluster',
      GlC: 'globular_cluster',
      OpC: 'open_galactic_cluster',
      'Cl*': 'open_galactic_cluster',
      'As*': 'group_of_stars',
      mul: 'multiple_objects',

      // Interstellar matter
      'PN?': 'planetary_nebula',
      PN: 'planetary_nebula',
      SNR: 'planetary_nebula',
      'SR?': 'planetary_nebula',
      ISM: 'interstellar_matter',

      // Galaxies
      PoG: 'part_of_galaxy',
      QSO: 'quasar',
      G: 'galaxy',

      dso: 'deep_sky',

      // Solar System
      Asa: 'artificial_satellite',
      Moo: 'moon',
      Sun: 'sun',
      Pla: 'planet',
      DPl: 'planet',
      Com: 'comet',
      MPl: 'minor_planet',
      SSO: 'minor_planet',

      Con: 'constellation'
    }
    for (const i in skySourceTypes) {
      if (skySourceTypes[i] in iconForType) {
        return process.env.BASE_URL + 'images/svg/target_types/' + iconForType[skySourceTypes[i]] + '.svg'
      }
    }
    return process.env.BASE_URL + 'images/svg/target_types/unknown.svg'
  },

  iconForSkySource: function (skySource) {
    return swh.iconForSkySourceTypes(skySource.types)
  },

  iconForObservation: function (obs) {
    if (obs && obs.target) {
      return this.iconForSkySource(obs.target)
    } else {
      return this.iconForSkySourceTypes(['reg'])
    }
  },

  cleanupOneSkySourceName: function (name, flags) {
    flags = flags || 4
    return Vue.prototype.$stel.designationCleanup(name, flags)
  },

  nameForSkySource: function (skySource) {
    if (!skySource || !skySource.names) {
      return '?'
    }
    return this.cleanupOneSkySourceName(skySource.names[0])
  },

  culturalNameToList: function (cn) {
    const res = []

    const formatNative = function (_cn) {
      if (cn.name_native && cn.name_pronounce) {
        return cn.name_native + ', <i>' + cn.name_pronounce + '</i>'
      }
      if (cn.name_native) {
        return cn.name_native
      }
      if (cn.name_pronounce) {
        return cn.name_pronounce
      }
    }

    const nativeName = formatNative(cn)
    if (cn.user_prefer_native && nativeName) {
      res.push(nativeName)
    }
    if (cn.name_translated) {
      res.push(cn.name_translated)
    }
    if (!cn.user_prefer_native && nativeName) {
      res.push(nativeName)
    }
    return res
  },

  namesForSkySource: function (ss, flags) {
    // Return a list of cleaned up names
    if (!ss || !ss.names) {
      return []
    }
    if (!flags) flags = 10
    let res = []
    if (ss.culturalNames) {
      for (const i in ss.culturalNames) {
        res = res.concat(this.culturalNameToList(ss.culturalNames[i]))
      }
    }
    res = res.concat(ss.names.map(n => Vue.prototype.$stel.designationCleanup(n, flags)))
    // Remove duplicates, this can happen between * and V* catalogs
    res = res.filter(function (v, i) { return res.indexOf(v) === i })
    res = res.filter(function (v, i) { return !v.startsWith('CON ') })
    // [guanxing patch] M-QZ：过滤四余内部代号（QZ luohou 等），只展示中文
    // 标签与拉丁名（Rahu/Ketu/Ziqi/Yuebei）。
    res = res.filter(function (v, i) { return !v.startsWith('QZ ') })
    return res
  },

  nameForSkySourceType: function (otype) {
    const $stel = Vue.prototype.$stel
    const res = $stel.otypeToStr(otype)
    return res || '未知类型'
  },

  nameForGalaxyMorpho: function (morpho) {
    const galTab = {
      E: 'Elliptical',
      SB: 'Barred Spiral',
      SAB: 'Intermediate Spiral',
      SA: 'Spiral',
      S0: 'Lenticular',
      S: 'Spiral',
      Im: 'Irregular',
      dSph: 'Dwarf Spheroidal',
      dE: 'Dwarf Elliptical'
    }
    for (const morp in galTab) {
      if (morpho.startsWith(morp)) {
        return galTab[morp]
      }
    }
    return ''
  },

  // [guanxing patch] M-SW18：分享链接生成函数随信息卡链接按钮移除（唯一消费者
  // 是已删除的信息卡分享弹层；cleanupOneSkySourceName 另有他处使用故保留）。

  // Return a SweObj matching a passed sky source JSON object if it's already instanciated in SWE
  skySource2SweObj: function (ss) {
    if (!ss || !ss.model) {
      return undefined
    }
    const $stel = Vue.prototype.$stel
    let obj
    if (ss.model === 'tle_satellite') {
      const id = 'NORAD ' + ss.model_data.norad_number
      obj = $stel.getObj(id)
    } else if (ss.model === 'constellation' && ss.model_data.iau_abbreviation) {
      const id = 'CON western ' + ss.model_data.iau_abbreviation
      obj = $stel.getObj(id)
    }
    if (!obj) {
      obj = $stel.getObj(ss.names[0])
    }
    if (!obj && ss.names[0].startsWith('Gaia DR2 ')) {
      const gname = ss.names[0].replace(/^Gaia DR2 /, 'GAIA ')
      obj = $stel.getObj(gname)
    }
    if (obj === null) return undefined
    return obj
  },

  lookupSkySourceByName: function (name) {
    return fetch(process.env.VUE_APP_NOCTUASKY_API_SERVER + '/api/v1/skysources/name/' + name)
      .then(function (response) {
        if (!response.ok) {
          throw response.body
        }
        return response.json()
      }, err => {
        throw err.response.body
      })
  },

  querySkySources: function (str, limit) {
    if (!limit) {
      limit = 10
    }
    return fetch(process.env.VUE_APP_NOCTUASKY_API_SERVER + '/api/v1/skysources/?q=' + str + '&limit=' + limit)
      .then(function (response) {
        if (!response.ok) {
          throw response.body
        }
        return response.json()
      }, err => {
        throw err.response.body
      })
  },

  sweObj2SkySource: function (obj) {
    // [guanxing patch] M-QZ：四余 circle 未实现 get_designations（obj.c
    // L343-350），常规路径会抛 "Can't find object without names"。按引擎
    // 对象指针识别，直接返回合成 SkySource（names[0] 为中文标签，供
    // 信息卡 title 与 qzsyDefForSkySource 使用），全程不发起外网请求。
    if (qzsyPtrs && obj && obj.swe_) {
      for (const def of this.QZSY_DEFS) {
        if (obj.v === qzsyPtrs[def.key]) {
          return Promise.resolve({
            model: 'circle',
            names: [def.label].concat(def.names),
            types: []
          })
        }
      }
    }
    const names = obj.designations()
    const that = this

    if (!names || !names.length) {
      throw new Error("Can't find object without names")
    }

    // Several artifical satellites share the same common name, so we use
    // the unambiguous NORAD number instead
    for (const j in names) {
      if (names[j].startsWith('NORAD ')) {
        const tmpName = names[0]
        names[0] = names[j]
        names[j] = tmpName
      }
    }

    const printErr = function (n) {
      console.log("Couldn't find online skysource data for name: " + n)

      const ss = obj.jsonData
      if (!ss.model_data) {
        ss.model_data = {}
      }
      // Names fixup
      let i
      for (i in ss.names) {
        if (ss.names[i].startsWith('GAIA')) {
          ss.names[i] = ss.names[i].replace(/^GAIA /, 'Gaia DR2 ')
        }
      }
      ss.culturalNames = obj.culturalDesignations()
      return ss
    }

    return that.lookupSkySourceByName(names[0]).then(res => {
      return res
    }, () => {
      if (names.length === 1) return printErr(names)
      return that.lookupSkySourceByName(names[1]).then(res => {
        return res
      }, () => {
        if (names.length === 2) return printErr(names)
        return that.lookupSkySourceByName(names[2]).then(res => {
          return res
        }, () => {
          return printErr(names[2])
        })
      })
    }).then(res => {
      res.culturalNames = obj.culturalDesignations()
      return res
    })
  },

  setSweObjAsSelection: function (obj) {
    const $stel = Vue.prototype.$stel
    $stel.core.selection = obj
    $stel.pointAndLock(obj)
  },

  // ── [guanxing patch] M-SW9：信息卡本地知识（中外星官/星座 lore） ──
  // 数据来自 skydata/lore/<skyculture>.json（构建期由 pipeline/build-lore.mjs
  // 生成），按激活星官模式加载，零外网。JSON 按模块缓存（缓存 Promise），
  // 加载失败缓存 null，不重试风暴。

  loreJsonCache: {},

  loreJsonFor: function (scId) {
    if (!(scId in this.loreJsonCache)) {
      const cache = this.loreJsonCache
      cache[scId] = fetch(process.env.BASE_URL + 'skydata/lore/' + scId + '.json')
        .then(res => {
          if (!res.ok) throw new Error('lore ' + scId + ' HTTP ' + res.status)
          return res.json()
        })
        .catch(err => {
          console.log('lore load failed for ' + scId + ':', err)
          cache[scId] = null
          return null
        })
    }
    return Promise.resolve(this.loreJsonCache[scId])
  },

  // 从选中对象解析知识块。选中链路两条 id 线索：
  //  - selectionId：store 快照 stel.selection 的句柄值（浏览器实测为数字指针，
  //    不含 id 语义；仅当它是字符串时才当 id 串解析）；
  //  - ss.names：SkySource 名表，恒星含 'HIP 21421'、星座对象含
  //    'CON chinese 001' / 'CON western Sco' —— 这是实际生效的解析来源。
  //  - 'CON ' 开头 → 星座对象自身被选中，直查 entries；
  //  - 'HIP N' → 恒星，查 hipIndex 得所属星官/星座（一星多官可多块）；
  //  - 其余（行星/月亮/卫星/DSO）无 lore，返回空数组（信息卡无知识块）。
  // 失败一律 resolve([])——知识块是增强信息，不允许它打断信息卡。
  loreBlocksForSkySource: async function (ss, selectionId) {
    const $stel = Vue.prototype.$stel
    if (!$stel || !$stel.core || !$stel.core.skycultures) return []
    const scId = $stel.core.skycultures.current_id
    if (!scId) return []
    const lore = await this.loreJsonFor(scId)
    if (!lore || !lore.entries) return []

    const idStr = (typeof selectionId === 'string' && selectionId) ||
      (ss && ss.names && (ss.names.find(n => /^CON /.test(n)) || ss.names.find(n => /^HIP \d+/.test(n)))) || ''
    const conMatch = /^CON [a-z0-9-]+ (\S+)$/.exec(idStr)
    const hipMatch = /HIP (\d+)/.exec(idStr) ||
      (ss && ss.names && ss.names.map(n => /HIP (\d+)/.exec(n)).find(Boolean))

    let entryKeys = []
    if (conMatch) {
      entryKeys = lore.entries[conMatch[1]] ? [conMatch[1]] : []
    } else if (hipMatch) {
      entryKeys = lore.hipIndex[hipMatch[1]] || []
    } else {
      return []
    }

    const label = scId === 'western' ? '星座' : (scId === 'chinese-28' ? '星宿' : '星官')
    const blocks = []
    for (const key of entryKeys) {
      const e = lore.entries[key]
      if (!e) continue
      let title = (conMatch ? label + ' · ' : '所属' + label + ' · ') + e.name
      if (scId === 'chinese-28' && e.group) title += '（' + e.group + '）'
      let text = e.sum || ''
      if (e.lore) text += (text ? '\n\n' : '') + e.lore
      if (scId === 'chinese-28' && e.crossZh && e.crossZh.length) {
        text += '\n\n对应西方星座：' + e.crossZh.join('、')
      }
      const blk = { title: title, text: text }
      // [guanxing patch] M-SW11：二十八宿逐宿古画（art 由 build-c28-art.mjs 挂，
      // 本地静态图零外网），chinese / chinese-28 两模式共用同一批图。
      if (e.art) {
        blk.image = process.env.BASE_URL + 'skydata/lore/' + e.art.file
        blk.imageCredit = e.art.credit
      }
      blocks.push(blk)
      if (scId === 'western') {
        // 「日期对应」（用户追加）：黄道 12 宫条目带占星日期，随块附上
        // 日期表由来的通用注记（lore.note：黄道等分十二宫 → 太阳月行一宫
        // → 生日定宫 + 岁差口径漂移与蛇夫座）；非黄道星座无 dates 不显示。
        if (e.dates) {
          let dt = e.dates
          if (lore.note) dt += '\n\n' + lore.note
          blocks.push({ title: '日期对应', text: dt })
        }
        if (e.stars) blocks.push({ title: '亮星', text: e.stars })
        if (e.dso) blocks.push({ title: '深空天体', text: e.dso })
        if (e.season) blocks.push({ title: '观测季节', text: e.season })
      }
    }
    // 「星宿与日期」（用户追加）：中国传统不按生日定宿——二十四节气
    // （太阳历）与昏中星定季节，十二星次是节气划分的黄道十二分段。
    // 挂 chinese-28.json 头部，随每宿知识块展示一次（放循环外防一星多宿重复）。
    if (scId === 'chinese-28' && lore.note) {
      blocks.push({ title: '星宿与日期', text: lore.note })
    }
    return blocks
  },

  // [guanxing patch] 上游先走第三方 GeoIP 在线回退服务（外网依赖、国内不可达、
  // jsonp 无超时会挂起），已断开：只走浏览器 geolocation，并加 10s timeout 防权限
  // 对话框挂起时 Promise 永不 settle；失败直接 reject 不猜测位置（默认北京由
  // store 的 currentLocation 兜底，见 AGENTS.md §4「定位失败不猜测」）。
  // 被断开的服务域名登记在 THIRD_PARTY_NOTICES.md §2（M10 断言禁止本文件
  // 出现该域名字面量）。
  getGeolocation: function () {
    console.log('Getting geolocalization')
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Cannot detect position'))
        return
      }
      navigator.geolocation.getCurrentPosition(function (position) {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        })
      }, function (err) {
        console.log('Browser geolocation failed: ' + err.message)
        reject(new Error('Cannot detect position'))
      }, { enableHighAccuracy: true, timeout: 10000, maximumAge: 600000 })
    })
  },

  delay: function (t, v) {
    return new Promise(function (resolve) {
      setTimeout(resolve.bind(null, v), t)
    })
  },

  // [guanxing patch] 上游调第三方反解析服务把坐标换成地名（外网依赖、把用户
  // 坐标发给第三方、国内可达性差），已断开：直接用坐标做显示名（zh.json 译为
  // 「纬 x° 经 y°」）。字段形状保持不变（date-time-picker 等仍消费 currentLocation）。
  // 被断开的服务域名登记在 THIRD_PARTY_NOTICES.md §2。
  geoCodePosition: function (pos, ctx) {
    console.log('Geocoding position... ')
    return Promise.resolve({
      short_name: ctx.$t('Lat {0}° Lon {1}°', [pos.lat.toFixed(3), pos.lng.toFixed(3)]),
      country: 'Unknown',
      lng: pos.lng,
      lat: pos.lat,
      alt: pos.alt ? pos.alt : 0,
      accuracy: pos.accuracy,
      street_address: ''
    })
  },

  getDistanceFromLatLonInM: function (lat1, lon1, lat2, lon2) {
    var deg2rad = function (deg) {
      return deg * (Math.PI / 180)
    }
    var R = 6371000 // Radius of the earth in m
    var dLat = deg2rad(lat2 - lat1)
    var dLon = deg2rad(lon2 - lon1)
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2)
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    var d = R * c // Distance in m
    return d
  },

  // Look for the next time starting from now on when the night Sky is visible
  // i.e. when sun is more than 10 degree below horizon.
  // If no such time was found (e.g. in a northern country in summer),
  // we default to current time.
  getTimeAfterSunset: function (stel) {
    const sun = stel.getObj('NAME Sun')
    const obs = stel.observer.clone()
    const utc = Math.floor(obs.utc * 24 * 60 / 5) / (24 * 60 / 5)
    let i
    for (i = 0; i < 24 * 60 / 5 + 1; i++) {
      obs.utc = utc + 1.0 / (24 * 60) * (i * 5)
      const sunRadec = sun.getInfo('RADEC', obs)
      const azalt = stel.convertFrame(obs, 'ICRF', 'OBSERVED', sunRadec)
      const alt = stel.anpm(stel.c2s(azalt)[1])
      if (alt < -13 * Math.PI / 180) {
        break
      }
    }
    if (i === 0 || i === 24 * 60 / 5 + 1) {
      return stel.observer.utc
    }
    return obs.utc
  },

  // Get the list of circumpolar stars in a given magnitude range
  //
  // Arguments:
  //   obs      - An observer.
  //   maxMag   - The maximum magnitude above which objects are discarded.
  //   filter   - a function called for each object returning false if the
  //              object must be filtered out.
  //
  // Return:
  //   An array SweObject. It is the responsibility of the caller to properly
  //   destroy all the objects of the list when they are not needed, by calling
  //   obj.destroy() on each of them.
  //
  // Example code:
  //   // Return all cicumpolar stars between mag -2 and 4
  //   let res = swh.getCircumpolarStars(this.$stel.observer, -2, 4)
  //   // Do something with the stars
  //   console.log(res.length)
  //   // Destroy the objects (don't forget this line!)
  //   res.map(e => e.destroy())
  getCircumpolarStars: function (obs, minMag, maxMag) {
    const $stel = Vue.prototype.$stel
    const filter = function (obj) {
      if (obj.getInfo('vmag', obs) <= minMag) {
        return false
      }
      const posJNOW = $stel.convertFrame(obs, 'ICRF', 'JNOW', obj.getInfo('radec'))
      const radecJNOW = $stel.c2s(posJNOW)
      const decJNOW = $stel.anpm(radecJNOW[1])
      if (obs.latitude >= 0) {
        return decJNOW >= Math.PI / 2 - obs.latitude
      } else {
        return decJNOW <= -Math.PI / 2 + obs.latitude
      }
    }
    return $stel.core.stars.listObjs(obs, maxMag, filter)
  },

  circumpolarMask: undefined,
  showCircumpolarMask: function (obs, show) {
    if (show === undefined) {
      show = true
    }
    const layer = Vue.prototype.$skyHintsLayer
    const $stel = Vue.prototype.$stel
    if (this.circumpolarMask) {
      layer.remove(this.circumpolarMask)
      this.circumpolarMask = undefined
    }
    if (show) {
      const diam = 2.0 * Math.PI - Math.abs(obs.latitude) * 2
      const shapeParams = {
        pos: [0, 0, obs.latitude > 0 ? -1 : 1, 0],
        frame: $stel.FRAME_JNOW,
        size: [diam, diam],
        color: [0.1, 0.1, 0.1, 0.8],
        border_color: [0.1, 0.1, 0.6, 1]
      }
      this.circumpolarMask = layer.add('circle', shapeParams)
    }
  }
}

export default swh
